/**
 * EventMap types for the ngit-ci dashboard.
 *
 * These model the subset of NIP-C1 (Nostr CI) events this dashboard renders:
 * Workflow Progress (kind 39842), Job Result (kind 9841) and Workflow Result
 * (kind 9842). Field names follow the NIP-C1 tag vocabulary so a Nostr event
 * can be mapped onto them losslessly.
 *
 * See docs/architecture.md for the protocol mapping and repo layout.
 */

import type { Event } from 'nostr-tools';

/** A single tag from a Nostr event: ["name", ...values]. */
export type Tag = [string, ...string[]];

/**
 * The raw Nostr event kinds the dashboard subscribes to.
 * - 39842 Workflow Progress  (addressable, coordinator)
 * - 9841  Job Result         (regular, compute provider)
 * - 9842  Workflow Result    (regular, coordinator)
 */
export const EVENT_KINDS = {
  WORKFLOW_PROGRESS: 39842,
  JOB_RESULT: 9841,
  WORKFLOW_RESULT: 9842,
} as const;

export type EventKind = (typeof EVENT_KINDS)[keyof typeof EVENT_KINDS];

/** Normalized trigger (`o` tag): why this attempt ran. */
export type Trigger = 'push' | 'pull_request' | 'schedule' | 'manual';

/** Workflow run status from the `status` tag of 39842. */
export type WorkflowStatus = 'queued' | 'in_progress' | 'concluded';

/** GitHub-API-aligned conclusion values from NIP-C1. */
export type Conclusion =
  | 'success'
  | 'failure'
  | 'neutral'
  | 'cancelled'
  | 'skipped'
  | 'timed_out'
  | 'startup_failure';

/** Repository address tag `a` = 30617:<repo-owner-pubkey>:<repo-id>. */
export interface RepoAddress {
  kind: number;
  pubkey: string;
  repoId: string;
}

/** A job's display status. */
export type JobStatus = 'queued' | 'in_progress' | 'concluded';

/**
 * A job within a workflow run (from a Job Result event, or job tags on
 * progress). Jobs are keyed per-instance by the Job Result event id; `jobId`
 * is the workflow-declared `job` tag (e.g. `go-test`), `name` the human
 * readable label (e.g. `Test/go-test-7`).
 */
export interface Job {
  /** Stable per-instance key: the Job Result event id (or job-id placeholder). */
  id: string;
  /** Workflow-declared job id (`job` tag), e.g. `go-test`. */
  jobId?: string;
  name?: string;
  conclusion?: Conclusion;
  status: JobStatus;
  exitCode?: number;
  logUrl?: string;
  runsOn?: string;
  queuedAt?: number;
  startedAt?: number;
  /** Small tail excerpt of the job log (`content`). */
  logTail?: string;
}

/** Workflow Progress event (kind 39842) parsed into dashboard state. */
export interface WorkflowProgress {
  kind: typeof EVENT_KINDS.WORKFLOW_PROGRESS;
  /** Source Nostr event id (used for dedup of regular kinds). */
  id: string;
  /** Workflow run id (`d` tag), unique per run attempt. */
  runId: string;
  status: WorkflowStatus;
  /** Present only when status is `concluded`. */
  conclusion?: Conclusion;
  /** Job ids currently executing (`in-progress` tags). */
  inProgressJobs: string[];
  /** Job ids that have completed (`q` tags with a job-id marker). */
  completedJobRefs: string[];
  commit: string;
  trigger?: Trigger;
  /** When execution began (`started_at` tag). */
  startedAt?: number;
  /** Raw NIP-34 repo address (`a` tag, `30617:<owner-pubkey>:<repo-id>`), '' when absent. */
  repoAddr: string;
  /** Repo id: the third colon-segment of the `a` tag, '' when absent. */
  repoId: string;
  /** Short repo owner: first 8 chars of the owner pubkey, '' when absent. */
  repoOwner: string;
  /** Raw git ref (`r` tag, e.g. `refs/heads/main`), '' when absent. */
  ref: string;
  /** Display branch: `refs/heads/` stripped; other refs kept readable. */
  branch: string;
  createdAt: number;
}

/** Job Result event (kind 9841) parsed into dashboard state. */
export interface JobResult {
  kind: typeof EVENT_KINDS.JOB_RESULT;
  /** Source Nostr event id (used for dedup of regular kinds). */
  id: string;
  /** Workflow run id, from the `q` tag `39842:<coordinator>:<run-id>`. */
  runRef: string;
  jobId: string;
  name?: string;
  conclusion?: Conclusion;
  /** Blossom URL of the full log (`logs` tag). */
  logsUrl?: string;
  /** When the job entered the queue (`queued_at` tag). */
  queuedAt?: number;
  /** When execution began (`started_at` tag). */
  startedAt?: number;
  exitCode?: number;
  runsOn?: string;
  /** Small tail excerpt of the job log (`content`). */
  logTail?: string;
  /** Raw NIP-34 repo address (`a` tag, `30617:<owner-pubkey>:<repo-id>`), '' when absent. */
  repoAddr: string;
  /** Repo id: the third colon-segment of the `a` tag, '' when absent. */
  repoId: string;
  /** Short repo owner: first 8 chars of the owner pubkey, '' when absent. */
  repoOwner: string;
  /** Raw git ref (`r` tag, e.g. `refs/heads/main`), '' when absent. */
  ref: string;
  /** Display branch: `refs/heads/` stripped; other refs kept readable. */
  branch: string;
  createdAt: number;
}

/** Workflow Result event (kind 9842) parsed into dashboard state. */
export interface WorkflowResult {
  kind: typeof EVENT_KINDS.WORKFLOW_RESULT;
  /** Source Nostr event id (used for dedup of regular kinds). */
  id: string;
  /** Workflow run id, from the non-ref `r` tag. */
  runId: string;
  conclusion: Conclusion;
  queuedAt?: number;
  startedAt?: number;
  commit: string;
  trigger?: Trigger;
  /**
   * Per-instance job refs quoted via `q` tags (each carries a job-id marker).
   * `eventId` is the quoted Job Result event id (the per-instance key);
   * `jobId` is the workflow-declared job id marker. Excludes manual/service
   * markers.
   */
  jobRefs: Array<{ eventId: string; jobId: string }>;
  /** Raw NIP-34 repo address (`a` tag, `30617:<owner-pubkey>:<repo-id>`), '' when absent. */
  repoAddr: string;
  /** Repo id: the third colon-segment of the `a` tag, '' when absent. */
  repoId: string;
  /** Short repo owner: first 8 chars of the owner pubkey, '' when absent. */
  repoOwner: string;
  /** Raw git ref (`r` tag, e.g. `refs/heads/main`), '' when absent. */
  ref: string;
  /** Display branch: `refs/heads/` stripped; other refs kept readable. */
  branch: string;
  createdAt: number;
}

/**
 * Union of the dashboard event types. The store keys these by `runId` for
 * progress/result and by `runId + jobId` for jobs.
 */
export type CiEvent = WorkflowProgress | JobResult | WorkflowResult;

/**
 * Minimal structural type for a raw Nostr event we accept for parsing.
 * `Event` from nostr-tools satisfies this shape at runtime.
 */
export type RawEvent = Pick<Event, 'kind' | 'pubkey' | 'created_at' | 'tags' | 'id' | 'content'>;
